import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Table, Empty, Tooltip } from "antd"
import type { TableRowSelection } from "antd/es/table/interface"
import { useNavigate } from "react-router-dom"
import { ColumnsType } from "antd/es/table"
import { useUserStore, User } from "stores/userStore"

import getColumns from "./columns"
import {
  getSkeletonColumns,
  getSkeletonData,
} from "shared/ui/LoadingSkeleton/LoadingSkeleton.component"
import { ProjectData } from "types/project.types"
import {
  ProjectTreeRow,
  canManageMembers,
  findFolderRow,
  folderPathOfRowId,
  folderRowId,
  isFolderRowId,
  projectRowsOf,
} from "utils/projectTree"

interface ProjectTableProps {
  rows: ProjectTreeRow[]
  searchTerm?: string
  isLoading: boolean
  isArchived: boolean
  handleUnarchive: (projectId: string, owner: string) => Promise<void> | void
  handleArchive: (projectId: string, owner: string) => Promise<void> | void
  onRenameFolder?: (folderPath: string) => void
  onDeleteFolder?: (folderPath: string, count: number) => void
  onMoveProject?: (project: ProjectData) => void
  /** Folder paths shown open; the table reports every expand and collapse. */
  openFolders: string[]
  onOpenFoldersChange: (folders: string[]) => void
  /** A folder to bring into view and highlight briefly, once it is listed. */
  revealFolder?: string | null
  /** Called once the reveal is done, or the folder isn't in the list. */
  onRevealDone?: () => void
  /**
   * Ticked project ids (never folder rows). Tick boxes show only when
   * `onSelectionChange` is given, and never on the archived list.
   */
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  /** An admin: may tick anyone's project, not just their own. */
  canManageAll?: boolean
}

const PAGE_SIZE = 10
// How long a revealed folder stays highlighted (the CSS fade matches).
const HIGHLIGHT_MS = 2000
const NO_SELECTION: string[] = []
const CANNOT_MANAGE = "Only the owner or an admin can change members"

const matchesSearch = (row: ProjectTreeRow, term: string): boolean => {
  if (row.kind !== "project") return false
  const haystack = (row.metadata?.Name ?? "").toLowerCase()
  return haystack.includes(term.toLowerCase())
}

const ProjectTable: React.FC<ProjectTableProps> = ({
  rows,
  searchTerm,
  isLoading,
  isArchived,
  handleUnarchive,
  handleArchive,
  onRenameFolder,
  onDeleteFolder,
  onMoveProject,
  openFolders,
  onOpenFoldersChange,
  revealFolder,
  onRevealDone,
  selectedIds = NO_SELECTION,
  onSelectionChange,
  canManageAll = false,
}) => {
  const user = useUserStore((state) => state.user) as User
  const navigate = useNavigate()

  const dataSource: ProjectTreeRow[] = useMemo(() => {
    const term = searchTerm?.trim() ?? ""
    if (!term) return rows
    // Searching lists the matches across folders in one flat list.
    return projectRowsOf(rows).filter((row) => matchesSearch(row, term))
  }, [rows, searchTerm])

  const tableRef = useRef<HTMLDivElement>(null)
  const [page, setPage] = useState<number>(1)
  // The folder path currently highlighted after a reveal.
  const [highlighted, setHighlighted] = useState<string | null>(null)

  const expandedRowKeys = useMemo(
    () => openFolders.map(folderRowId),
    [openFolders]
  )

  // Once the list has loaded: open the folder and its parents, turn to the page
  // holding it and mark it. The folder is looked up as the tree files it
  // (ignoring case), and each row is opened under its own spelling so the
  // saved keys match rows. Pages count top-level rows, so it's the page of the
  // folder's top parent. A folder that isn't listed (or an empty list) just
  // ends the reveal, so it can't fire later on another view; the caller keeps
  // isLoading on until the list has been asked for.
  useEffect(() => {
    if (!revealFolder || isLoading) return
    const found = findFolderRow(dataSource, revealFolder)
    if (!found) {
      onRevealDone?.()
      return
    }
    const folderPath = found.row.folderPath
    const toOpen = found.chain
      .map((row) => row.folderPath)
      .filter((path) => !openFolders.includes(path))
    if (toOpen.length > 0) onOpenFoldersChange([...openFolders, ...toOpen])
    setPage(Math.floor(found.topIndex / PAGE_SIZE) + 1)
    setHighlighted(folderPath)
    onRevealDone?.()
  }, [
    revealFolder,
    isLoading,
    dataSource,
    openFolders,
    onOpenFoldersChange,
    onRevealDone,
  ])

  // Scroll the highlighted folder into view; the highlight fades after ~2 s.
  useEffect(() => {
    if (!highlighted) return
    const key = folderRowId(highlighted)
    const row = Array.from(
      tableRef.current?.querySelectorAll<HTMLElement>("tr[data-row-key]") ?? []
    ).find((el) => el.getAttribute("data-row-key") === key)
    // jsdom has no scrollIntoView.
    row?.scrollIntoView?.({ block: "center", behavior: "smooth" })
    const timer = window.setTimeout(() => setHighlighted(null), HIGHLIGHT_MS)
    return () => window.clearTimeout(timer)
  }, [highlighted])

  const columns: ColumnsType<ProjectTreeRow> = getColumns(
    isArchived,
    handleUnarchive,
    handleArchive,
    user,
    { onRenameFolder, onDeleteFolder, onMoveProject }
  )

  // A project can be ticked by its owner or an admin; a folder when anything
  // inside it can (ticking it then ticks just those).
  const username = user?.username ?? ""
  const canTick = useCallback(
    (record: ProjectTreeRow): boolean =>
      record.kind === "project"
        ? canManageMembers(record, username, canManageAll)
        : projectRowsOf(record.children).some((row) =>
            canManageMembers(row, username, canManageAll)
          ),
    [username, canManageAll]
  )

  // Tree selection: a folder ticks its projects and shows half-ticked when
  // only some are. Ticks are held by the caller, so they survive collapsing a
  // folder and turning the page. Off on the archived list (archived projects
  // keep no members) and while the skeleton shows.
  const rowSelection: TableRowSelection<ProjectTreeRow> | undefined =
    onSelectionChange && !isArchived && !isLoading
      ? {
          checkStrictly: false,
          selectedRowKeys: selectedIds,
          // Only projects are reported; a folder is just a way to tick them.
          onChange: (keys) =>
            onSelectionChange(
              keys.map(String).filter((key) => !isFolderRowId(key))
            ),
          getCheckboxProps: (record) => ({ disabled: !canTick(record) }),
          renderCell: (_checked, record, _index, node) => {
            // An empty folder has nothing to explain.
            const empty = record.kind === "folder" && record.count === 0
            if (canTick(record) || empty) return node
            return (
              <Tooltip title={CANNOT_MANAGE}>
                <span>{node}</span>
              </Tooltip>
            )
          },
          // A click beside the box doesn't open the project.
          onCell: () => ({ onClick: (e) => e.stopPropagation() }),
        }
      : undefined

  const skeletonColumns = getSkeletonColumns<ProjectTreeRow>(columns)
  const skeletonData = getSkeletonData<ProjectTreeRow>(5) as ProjectTreeRow[]

  const CustomNoData = () => (
    <div className="p-10">
      <Empty description={<span>No Projects Available.</span>} />
    </div>
  )

  const handleRowClick = useCallback(
    (record: ProjectTreeRow): void => {
      if (record.kind !== "project") return
      if (isArchived || !record.id) return
      navigate(`/home/${record.id}`)
    },
    [isArchived, navigate]
  )

  return (
    <div ref={tableRef}>
      <Table<ProjectTreeRow>
        size="small"
        className="project-table"
        rowClassName={(record) => {
          if (record.kind === "folder") {
            return record.folderPath === highlighted
              ? "project-table-folder-row project-table-row--revealed"
              : "project-table-folder-row"
          }
          return !isArchived ? "cursor-pointer" : ""
        }}
        rowKey="id"
        rowSelection={rowSelection}
        dataSource={isLoading ? skeletonData : dataSource}
        columns={isLoading ? skeletonColumns : columns}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) =>
            onOpenFoldersChange(
              keys.map(String).filter(isFolderRowId).map(folderPathOfRowId)
            ),
        }}
        pagination={{
          current: page,
          onChange: setPage,
          pageSize: PAGE_SIZE,
          hideOnSinglePage: true,
          showSizeChanger: false,
          simple: true,
        }}
        locale={{
          emptyText: <CustomNoData />,
        }}
        onRow={(record) => ({
          onClick: () => !isLoading && handleRowClick(record),
        })}
      />
    </div>
  )
}

export default ProjectTable
